import { useParams, useNavigate } from '@tanstack/react-router'
import { toast } from '@/hooks/use-toast'
import { FnsActionForm } from '@/features/forms-and-surveys/components'
import {
  useSurveyById,
  useCreateSurvey,
  useUpdateSurvey,
  useFormById,
  useFnsMode,
  useCreateForm,
  useUpdateForm,
} from '@/features/forms-and-surveys/hooks'
import { SurveyMutationInput } from '@/features/forms-and-surveys/types'

function FormActionPage() {
  const { formId } = useParams({ strict: false })
  const navigate = useNavigate()
  const isEditMode = !!formId

  const { data: form } = useFormById(formId || '')
  const { createForm, loading: createLoading } = useCreateForm()
  const { updateForm, loading: updateLoading } = useUpdateForm()

  const loading = createLoading || updateLoading

  const handleSubmit = async (data: SurveyMutationInput) => {
    const { type, ...formData } = data
    try {
      let result

      if (isEditMode && formId) {
        result = await updateForm(formId, formData)
        if (result?.data?.fnsServiceUpdateFormForAdmin) {
          toast({
            title: 'Form updated successfully',
            variant: 'success',
          })
          navigate({ to: '/forms/$formId', params: { formId } })
        } else {
          toast({
            title: 'Failed to update form',
            variant: 'destructive',
          })
        }
      } else {
        result = await createForm(formData)
        if (result?.data?.fnsServiceCreateFormForAdmin) {
          const newFormId = result.data.fnsServiceCreateFormForAdmin.id
          toast({
            title: 'Form created successfully',
            variant: 'success',
          })
          navigate({
            to: '/forms/$formId',
            params: { formId: newFormId },
          })
        } else {
          toast({
            title: 'Failed to create form',
            variant: 'destructive',
          })
        }
      }
    } catch (_error) {
      toast({
        title: 'Failed to save form',
        variant: 'destructive',
      })
    }
  }

  const handleCancel = () => {
    navigate({ to: '..' })
  }

  return (
    <FnsActionForm
      data={form}
      loading={loading}
      isEditMode={isEditMode}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  )
}

function SurveyActionPage() {
  const { surveyId } = useParams({ strict: false })
  const navigate = useNavigate()
  const isEditMode = !!surveyId

  const { data: survey } = useSurveyById(surveyId || '')
  const { createSurvey, loading: createLoading } = useCreateSurvey()
  const { updateSurvey, loading: updateLoading } = useUpdateSurvey()

  const loading = createLoading || updateLoading

  const handleSubmit = async (data: SurveyMutationInput) => {
    try {
      let result

      if (isEditMode && surveyId) {
        result = await updateSurvey(surveyId, data)
        if (result?.data?.fnsServiceUpdateSurveyForAdmin) {
          toast({
            title: 'Survey updated successfully',
            variant: 'success',
          })
          navigate({ to: '/surveys/$surveyId', params: { surveyId } })
        } else {
          toast({
            title: 'Failed to update survey',
            variant: 'destructive',
          })
        }
      } else {
        result = await createSurvey(data)
        if (result?.data?.fnsServiceCreateSurveyForAdmin) {
          const newSurveyId = result.data.fnsServiceCreateSurveyForAdmin.id
          toast({
            title: 'Survey created successfully',
            variant: 'success',
          })
          navigate({
            to: '/surveys/$surveyId',
            params: { surveyId: newSurveyId },
          })
        } else {
          toast({
            title: 'Failed to create survey',
            variant: 'destructive',
          })
        }
      }
    } catch (_error) {
      toast({
        title: 'Failed to save survey',
        variant: 'destructive',
      })
    }
  }

  const handleCancel = () => {
    navigate({ to: '..' })
  }

  return (
    <FnsActionForm
      data={survey}
      loading={loading}
      isEditMode={isEditMode}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  )
}

function FnsActionPage() {
  const mode = useFnsMode()

  if (mode === 'survey') {
    return <SurveyActionPage />
  }

  return <FormActionPage />
}

export default FnsActionPage
